import type {
  ICountry,
  IDistrict,
  IProvince,
  ISubDistrict,
} from 'features/Address/model'
import type { BranchAddressLoadingProps } from 'features/Branch'
import type { ControllerRenderProps } from 'react-hook-form'
import type { CompanyForFormSubmit, OptionProps } from 'types'

import { isEmpty } from 'lodash'
import React, { useState, useEffect } from 'react'
import { useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { TextareaInput } from 'components'
import {
  addressOptionPropsFormatter,
  addressOptionFormatter,
} from 'helpers/common'
import * as utils from 'helpers/getAddress'

import { useCompanyAddressEdit } from './useCompanyAddressEdit'
import { InputFormControl } from './InputFormControl'
import { InputAddress } from './InputAddress'
import { InputCountry } from './InputCountry'
import { InputProvince } from './InputProvince'
import { InputDistrict } from './InputDistrict'
import { InputSubDistrict } from './InputSubDistrict'
import { InputPostalCode } from './InputPostalCode'

const CompanyAddress: React.FC<BranchAddressLoadingProps> = ({
  setAddressLoading,
}: BranchAddressLoadingProps): JSX.Element => {
  const { errors, watch, setValue } = useFormContext<CompanyForFormSubmit>()
  const [countries, setCountries] = useState<ICountry[]>([])
  const [provinces, setProvinces] = useState<IProvince[]>([])
  const [districts, setDistricts] = useState<IDistrict[]>([])
  const [subDistricts, setSubDistricts] = useState<ISubDistrict[]>([])
  const [isCountriesLoading, setIsCountriesLoading] = useState<boolean>(false)
  const [isProvincesLoading, setIsProvincesLoading] = useState<boolean>(false)
  const [isDistrictsLoading, setIsDistrictsLoading] = useState<boolean>(false)
  const [isSubDistrictsLoading, setIsSubDistrictsLoading] =
    useState<boolean>(false)
  const { country, province, district, subDistrict } = watch([
    'country',
    'province',
    'district',
    'subDistrict',
  ])
  const [addressChecking, setAddressChecking] = useState<boolean>(false)
  const [provinceChecking, setProvinceChecking] = useState<boolean>(false)
  const [districtChecking, setDistrictChecking] = useState<boolean>(false)
  const [subDistrictChecking, setSubDistrictChecking] = useState<boolean>(false)
  const [postalCodeChecking, setPostalCodeChecking] = useState<boolean>(false)

  const { t } = useTranslation(['common', 'company'])

  const emptyId = { id: '' }
  const base = 'f-company__form'

  const getCountries = async () => {
    setIsCountriesLoading(true)
    const { countries } = await utils.getCountries()
    const hasChildren = !isEmpty(countries)

    if (hasChildren) {
      const firstCountry = countries[0]
      const country = {
        id: firstCountry.id,
        countryTh: firstCountry.native,
        countryEn: firstCountry.name,
      }
      setValue('country', country)
      getProvinces(country.id)
    }

    setCountries(countries)
    setIsCountriesLoading(false)
  }

  const getProvinces = async (countryId: string) => {
    setIsProvincesLoading(true)
    const { provinces } = await utils.getProvinces(countryId)
    setProvinces(provinces)
    setIsProvincesLoading(false)
  }

  const getDistricts = async (provinceId: string) => {
    setIsDistrictsLoading(true)
    const { districts } = await utils.getDistricts(provinceId)
    setDistricts(districts)
    setIsDistrictsLoading(false)
  }

  const getSubDistricts = async (districtId: string) => {
    setIsSubDistrictsLoading(true)
    const { subDistricts } = await utils.getSubDistricts(districtId)
    setSubDistricts(subDistricts)
    setIsSubDistrictsLoading(false)
  }

  const handleSubDistrictChange = (option: OptionProps) => {
    const postalCode = utils.handlePostalCode(option, subDistricts)
    setValue('postalCode', postalCode)
  }

  const getErrorMsg = (value: string, msg?: string) =>
    utils.getErrorMsg(value, msg)

  useEffect(() => {
    getCountries()
  }, [])

  useCompanyAddressEdit(
    countries,
    setProvinces,
    setDistricts,
    setSubDistricts,
    setIsProvincesLoading,
    setIsDistrictsLoading,
    setIsSubDistrictsLoading,
  )

  useEffect(() => {
    if (isDistrictsLoading) {
      setAddressLoading(isDistrictsLoading)
    }

    return () => {
      setAddressLoading(false)
    }
  }, [isDistrictsLoading])

  const renderInputAddress = (field: ControllerRenderProps) => (
    <InputAddress
      isAddressChecking={addressChecking}
      setIsAddressChecking={setAddressChecking}
      field={field}
    />
  )

  const renderInputCountry = (field: ControllerRenderProps) => (
    <InputCountry
      countries={countries}
      isCountriesLoading={isCountriesLoading}
      field={field}
      handleOptions={addressOptionPropsFormatter}
      handleSelected={addressOptionFormatter}
      getProvinces={getProvinces}
      emptyId={emptyId}
    />
  )

  const renderInputProvince = (field: ControllerRenderProps) => (
    <InputProvince
      provinces={provinces}
      isProvincesLoading={isProvincesLoading}
      isProvinceChecking={provinceChecking}
      setIsProvinceChecking={setProvinceChecking}
      field={field}
      handleOptions={addressOptionPropsFormatter}
      handleSelected={addressOptionFormatter}
      getDistricts={getDistricts}
      emptyId={emptyId}
    />
  )

  const renderInputDistrict = (field: ControllerRenderProps) => (
    <InputDistrict
      districts={districts}
      isDistrictsLoading={isDistrictsLoading}
      isDistrictChecking={districtChecking}
      setIsDistrictChecking={setDistrictChecking}
      field={field}
      handleOptions={addressOptionPropsFormatter}
      handleSelected={addressOptionFormatter}
      getSubDistricts={getSubDistricts}
      emptyId={emptyId}
    />
  )

  const renderInputSubDistrict = (field: ControllerRenderProps) => (
    <InputSubDistrict
      subDistricts={subDistricts}
      isSubDistrictsLoading={isSubDistrictsLoading}
      isSubDistrictChecking={subDistrictChecking}
      setIsSubDistrictChecking={setSubDistrictChecking}
      field={field}
      handleOptions={addressOptionPropsFormatter}
      handleSelected={addressOptionFormatter}
      handleSubDistrictChange={handleSubDistrictChange}
    />
  )

  const renderInputPostalCode = (field: ControllerRenderProps) => (
    <InputPostalCode
      isPostalCodeChecking={postalCodeChecking}
      setIsPostalCodeChecking={setPostalCodeChecking}
      field={field}
    />
  )

  return (
    <div className={`${base}__section`}>
      <span className={`${base}__title`}>{t('company:address.label')}</span>

      <div>
        <div className={`${base}__info`}>
          <InputFormControl
            className={`${base}__item`}
            name='address'
            label={t('common:address.label')}
            htmlFor='branch address'
            render={renderInputAddress}
            type='text'
            required
          />

          <InputFormControl
            className={`${base}__item`}
            name='country'
            label={t('common:address.country.label')}
            htmlFor='branch country'
            render={renderInputCountry}
            required
            isInputGroup
            errorMsg={getErrorMsg(country.id, errors.country?.id?.message)}
          />

          <InputFormControl
            className={`${base}__item`}
            name='province'
            label={t('common:address.province.label')}
            htmlFor='branch province'
            render={renderInputProvince}
            required
            isInputGroup
            errorMsg={getErrorMsg(province.id, errors.province?.id?.message)}
          />

          <InputFormControl
            className={`${base}__item`}
            name='district'
            label={t('common:address.district.label')}
            htmlFor='branch district'
            render={renderInputDistrict}
            required
            isInputGroup
            errorMsg={getErrorMsg(district.id, errors.district?.id?.message)}
          />

          <InputFormControl
            className={`${base}__item`}
            name='subDistrict'
            label={t('common:address.subdistrict.label')}
            htmlFor='branch sub district'
            render={renderInputSubDistrict}
            required
            isInputGroup
            errorMsg={getErrorMsg(
              subDistrict.id,
              errors.subDistrict?.id?.message,
            )}
          />

          <InputFormControl
            className={`${base}__item`}
            name='postalCode'
            label={t('common:address.postalCode.label')}
            htmlFor='branch postalCode'
            render={renderInputPostalCode}
            type='text'
            required
          />
        </div>

        <InputFormControl
          className={`${base}__detail`}
          name='detail'
          label={t('common:address.remark.label')}
          htmlFor='branch detail'
          asRef={TextareaInput}
          placeholder={t('common:address.remark.placeholder')}
          hasCountable
          maxLength={1000}
          fullWidth
        />
      </div>
    </div>
  )
}

export default CompanyAddress
